import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';

const formatDate = value => {
  if (!value) return null;
  const date =
    value?.toDate?.() ??
    (typeof value === 'number' || typeof value === 'string'
      ? new Date(value)
      : value);
  if (!date || Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export default function ProfileDetailPage() {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = usePublicAuth();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [counts, setCounts] = useState({
    senderRequests: 0,
    travelerRequests: 0,
    senderCompleted: 0,
    travelerCompleted: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fallbackName =
    location.state?.fallbackName ||
    profile?.displayName ||
    t('profile.heroBadge');

  useEffect(() => {
    if (!userId) {
      setError(t('common.error'));
      setLoading(false);
      return;
    }

    const userRef = doc(db, 'users', userId);
    const unsub = onSnapshot(
      userRef,
      snapshot => {
        if (snapshot.exists()) {
          setProfile({ id: snapshot.id, ...snapshot.data() });
        } else {
          setError(t('common.error'));
        }
        setLoading(false);
      },
      err => {
        console.error('Profile listener error', err);
        setError(t('common.error'));
        setLoading(false);
      },
    );

    return () => unsub();
  }, [userId]);

  useEffect(() => {
    const fetchCounts = async () => {
      if (!userId) return;
      try {
        const requesterSnap = await getDocs(
          query(collection(db, 'requests'), where('requesterId', '==', userId)),
        );
        const ownerSnap = await getDocs(
          query(collection(db, 'requests'), where('tripOwnerId', '==', userId)),
        );

        let senderCompleted = 0;
        let travelerCompleted = 0;

        requesterSnap.docs.forEach(docSnap => {
          const data = docSnap.data();
          if ((data.status || '').toLowerCase() === 'completed') {
            if (data.tripType === 'shipping') senderCompleted += 1;
            if (data.tripType === 'carrying') travelerCompleted += 1;
          }
        });

        ownerSnap.docs.forEach(docSnap => {
          const data = docSnap.data();
          if ((data.status || '').toLowerCase() === 'completed') {
            if (data.tripType === 'carrying') senderCompleted += 1;
            if (data.tripType === 'shipping') travelerCompleted += 1;
          }
        });

        setCounts({
          senderRequests: requesterSnap.size,
          travelerRequests: ownerSnap.size,
          senderCompleted,
          travelerCompleted,
        });
      } catch (err) {
        console.error('Failed to load counts', err);
      }
    };

    fetchCounts();
  }, [userId]);

  useEffect(() => {
    const fetchReviews = async () => {
      if (!userId) return;
      try {
        const reviewsRef = collection(db, 'users', userId, 'reviews');
        const reviewsQuery = query(
          reviewsRef,
          orderBy('createdAt', 'desc'),
          limit(25),
        );
        const snapshot = await getDocs(reviewsQuery);
        setReviews(
          snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() })),
        );
      } catch (err) {
        console.error('Failed to load reviews', err);
        // fallback without orderBy
        try {
          const fallbackSnap = await getDocs(
            query(collection(db, 'users', userId, 'reviews'), limit(25)),
          );
          setReviews(
            fallbackSnap.docs.map(docSnap => ({
              id: docSnap.id,
              ...docSnap.data(),
            })),
          );
        } catch (fallbackError) {
          console.error('Fallback review load failed', fallbackError);
        }
      }
    };

    fetchReviews();
  }, [userId]);

  const rating = useMemo(() => {
    const value =
      profile?.averageRating ??
      profile?.rating ??
      profile?.stats?.rating ??
      (reviews[0]?.rating || 0);
    return value ? Number(value) : 0;
  }, [profile, reviews]);

  const reviewCount =
    profile?.totalReviews ??
    profile?.reviewsCount ??
    profile?.stats?.reviewsCount ??
    reviews.length;

  const joinDate = formatDate(profile?.createdAt);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      <section className="pt-28 pb-12 bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <button
                onClick={() => navigate(-1)}
                className="text-xs font-semibold uppercase tracking-[0.4em] text-gray-400 dark:text-slate-400"
              >
                {t('profileDetail.back')}
              </button>
              <h1 className="text-4xl font-black text-gray-900 dark:text-white mt-2">
                {profile?.displayName || fallbackName}
              </h1>
              {profile?.username ? (
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  @{profile.username}
                </p>
              ) : null}
            </div>
            {joinDate && (
              <p className="text-xs font-semibold uppercase tracking-[0.4em] text-gray-400 dark:text-slate-400">
                {t('profileDetail.memberSince', { date: joinDate })}
              </p>
            )}
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-12 space-y-10">
        {error ? (
          <div className="rounded-3xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6 text-red-700 dark:text-red-400">
            {error}
          </div>
        ) : null}

        <section className="rounded-3xl bg-white dark:bg-slate-900 shadow-lg border border-gray-100 dark:border-slate-800 p-8 grid grid-cols-1 md:grid-cols-[220px,1fr] gap-8">
          <div className="flex flex-col items-center text-center">
            <div className="h-32 w-32 rounded-full bg-black dark:bg-slate-700 text-yellow-400 flex items-center justify-center text-4xl font-black">
              {(profile?.displayName || fallbackName)
                .split(' ')
                .map(part => part.charAt(0).toUpperCase())
                .slice(0, 2)
                .join('')}
            </div>
            <p className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">
              {profile?.displayName || fallbackName}
            </p>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              {profile?.email || t('profileDetail.noEmail')}
            </p>
            {profile?.verificationStatus ? (
              <span
                className={`mt-3 rounded-full px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] ${
                  profile.verificationStatus === 'verified'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : profile.verificationStatus === 'pending'
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                    : 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400'
                }`}
              >
                {profile.verificationStatus}
              </span>
            ) : null}
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800 p-4">
                <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-[0.3em]">
                  {t('profileDetail.rating')}
                </p>
                <p className="text-3xl font-black text-gray-900 dark:text-white mt-1">
                  {rating ? rating.toFixed(1) : '—'}
                </p>
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  {t('profile.reviewsCount', { count: reviewCount })}
                </p>
              </div>
              <div className="rounded-2xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800 p-4">
                <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-[0.3em]">
                  {t('profileDetail.completedDeliveries')}
                </p>
                <p className="text-3xl font-black text-gray-900 dark:text-white mt-1">
                  {counts.senderCompleted + counts.travelerCompleted}
                </p>
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  {t('profileDetail.completedSenderTraveler', {
                    sender: counts.senderCompleted,
                    traveler: counts.travelerCompleted,
                  })}
                </p>
              </div>
            </div>

            {profile?.bio ? (
              <div className="rounded-2xl border border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-800 p-6">
                <p className="text-sm font-semibold uppercase tracking-[0.3em] text-gray-400 dark:text-slate-400">
                  {t('profileDetail.about')}
                </p>
                <p className="mt-3 text-gray-700 dark:text-slate-300 leading-relaxed">
                  {profile.bio}
                </p>
              </div>
            ) : null}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                {
                  label: t('profileDetail.completedSender'),
                  value: counts.senderCompleted,
                },
                {
                  label: t('profileDetail.completedTraveler'),
                  value: counts.travelerCompleted,
                },
              ].map(metric => (
                <div
                  key={metric.label}
                  className="rounded-2xl border border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-800 p-4 text-center"
                >
                  <p className="text-2xl font-black text-gray-900 dark:text-white">
                    {metric.value}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    {metric.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-3xl bg-white dark:bg-slate-900 shadow-lg border border-gray-100 dark:border-slate-800 p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-400 dark:text-slate-400">
                {t('profileDetail.recentReviews')}
              </p>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white">
                {t('profileDetail.communitySays')}
              </h2>
            </div>
            {user?.uid === userId && (
              <button
                className="rounded-2xl border border-gray-200 dark:border-slate-700 px-4 py-2 text-xs font-semibold text-gray-600 dark:text-slate-400 hover:border-yellow-200 dark:hover:border-yellow-600"
                onClick={() => navigate('/profile')}
              >
                {t('profileDetail.backToProfile')}
              </button>
            )}
          </div>

          {loading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, idx) => (
                <div
                  key={idx}
                  className="h-24 w-full animate-pulse rounded-2xl bg-gray-100 dark:bg-slate-800"
                />
              ))}
            </div>
          ) : reviews.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-slate-400">
              {t('profileDetail.noReviews')}
            </p>
          ) : (
            <div className="space-y-4">
              {reviews.map(review => (
                <div
                  key={review.id}
                  className="rounded-2xl border border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800 p-6"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {review.reviewerName || t('profileDetail.anonymous')}
                      </p>
                      {review.role ? (
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                          {review.role === 'sender'
                            ? t('profileDetail.senderRole')
                            : t('profileDetail.travelerRole')}
                        </p>
                      ) : null}
                    </div>
                    <div className="text-yellow-500 font-black">
                      {'⭐'.repeat(Math.max(1, Math.round(review.rating || 0)))}
                      {review.rating ? (
                        <span className="ml-1 text-xs text-gray-700 dark:text-slate-300">
                          {Number(review.rating).toFixed(1)}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {review.comment ? (
                    <p className="mt-4 text-sm text-gray-700 dark:text-slate-300 leading-relaxed">
                      {review.comment}
                    </p>
                  ) : null}
                  {review.createdAt ? (
                    <p className="mt-3 text-xs text-gray-400 dark:text-slate-500">
                      {formatDate(
                        review.createdAt?.toDate
                          ? review.createdAt.toDate()
                          : review.createdAt,
                      )}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
